import {
  BundleNamedSlotList,
  OnsiteUpsellNamedSlotList,
  OnsiteUpsellProductSlotList,
} from "./components";

// Slots a template may reference; everything else in the markup is literal HTML.
export const OnsiteUpsellTemplateSlotWhitelist = [
  ...OnsiteUpsellProductSlotList,
  ...OnsiteUpsellNamedSlotList,
] as const;

export const BundleTemplateSlotWhitelist = [
  ...OnsiteUpsellProductSlotList,
  ...BundleNamedSlotList,
  "variant-select",
] as const;

const TEMPLATE_SLOTS = {
  onsiteUpsell: OnsiteUpsellTemplateSlotWhitelist,
  bundleOnsiteUpsell: BundleTemplateSlotWhitelist,
} as const;

export type TemplateComponentType = keyof typeof TEMPLATE_SLOTS;

export function allowedTemplateSlots(
  type: TemplateComponentType
): readonly string[] {
  return TEMPLATE_SLOTS[type];
}

const SLOT_TOKEN_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

export function extractTemplateSlots(template: string): string[] {
  return Array.from(template.matchAll(SLOT_TOKEN_PATTERN), (match) => match[1]);
}

export function findUnknownTemplateSlots(
  template: string,
  type: TemplateComponentType
): string[] {
  const whitelist = new Set<string>(TEMPLATE_SLOTS[type]);
  return Array.from(
    new Set(
      extractTemplateSlots(template)
        // ignore handlebars block open/close (e.g. `#each products`, `/each`)
        .filter((slot) => !slot.startsWith("#") && !slot.startsWith("/"))
        .filter((slot) => !whitelist.has(slot))
    )
  );
}

// Every slot must stay in the template: each degrades to empty (and
// hides via `:empty`) when it has no value, so keeping them is always safe, while
// dropping one silently removes that element — e.g. losing `product.compareAtPrice`
// takes the comparison price with it. Returns the slots not present.
export function findMissingTemplateSlots(
  template: string,
  type: TemplateComponentType
): string[] {
  const present = new Set(extractTemplateSlots(template));
  return TEMPLATE_SLOTS[type].filter((slot) => !present.has(slot));
}

// Same markers as ig-utils' UPSELL_EACH_OPEN/CLOSE; ig-utils depends on this
// package, so they can't be imported from there.
const EACH_OPEN = "{{#each products}}";
const EACH_CLOSE = "{{/each}}";
const BUNDLE_ROW_SLOTS = new Set<string>([
  ...OnsiteUpsellProductSlotList,
  "checkbox",
  "variant-select",
]);
const BUNDLE_SHELL_SLOTS = new Set<string>(
  BundleNamedSlotList.filter((slot) => slot !== "checkbox")
);

// Without its products block the renderer repeats the whole bundle per product,
// so a bundle needs exactly one, with row slots inside and shell slots outside.
export function findBundleStructureIssues(template: string): {
  hasProductsBlock: boolean;
  rowSlotsOutside: string[];
  shellSlotsInside: string[];
} {
  const start = template.indexOf(EACH_OPEN);
  const end = template.indexOf(EACH_CLOSE);
  const hasProductsBlock =
    template.split(EACH_OPEN).length === 2 &&
    template.split(EACH_CLOSE).length === 2 &&
    start < end;
  if (!hasProductsBlock) {
    return { hasProductsBlock, rowSlotsOutside: [], shellSlotsInside: [] };
  }

  const row = extractTemplateSlots(
    template.slice(start + EACH_OPEN.length, end)
  );
  const shell = extractTemplateSlots(
    template.slice(0, start) + template.slice(end + EACH_CLOSE.length)
  );
  return {
    hasProductsBlock,
    rowSlotsOutside: [...new Set(shell.filter((s) => BUNDLE_ROW_SLOTS.has(s)))],
    shellSlotsInside: [
      ...new Set(row.filter((s) => BUNDLE_SHELL_SLOTS.has(s))),
    ],
  };
}
